import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getAudits, getAudit, createAudit, updateAudit, deleteAudit, getAllUsers, archiveResource, exportAudit } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { auditScoreColor } from '@/utils/styleHelpers';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { emptyStateMsg } from '@/utils/styleHelpers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  Plus,
  ArrowLeft,
  Search,
  Filter,
  Trash2,
  ClipboardCheck,
  CheckCircle2,
  AlertCircle,
  Save,
  Users,
  UserPlus,
  X,
  Archive,
  Download,
  Mail,
  FileText,
  FileSpreadsheet,
  Clock
} from 'lucide-react';

const industries = [
  { value: 'logistics', label: 'Logistics' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
];

const sixSCategories = [
  { key: 'sort', label: 'Sort (Seiri)', description: 'Remove unnecessary items', icon: '整' },
  { key: 'set_in_order', label: 'Set in Order (Seiton)', description: 'Organize remaining items', icon: '頓' },
  { key: 'shine', label: 'Shine (Seiso)', description: 'Clean and inspect', icon: '清' },
  { key: 'standardize', label: 'Standardize (Seiketsu)', description: 'Create standards', icon: '清' },
  { key: 'sustain', label: 'Sustain (Shitsuke)', description: 'Maintain discipline', icon: '躾' },
  { key: 'safety', label: 'Safety', description: 'Ensure workplace safety', icon: '安' },
];

const getScoreColor = (percentage) => {
  if (percentage >= 80) return { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Excellent' };
  if (percentage >= 60) return { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Good' };
  if (percentage >= 40) return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Fair' };
  return { bg: 'bg-rose-50', text: 'text-rose-600', label: 'Needs Improvement' };
};

// Audits List Page
export const AuditsListPage = () => {
  const { isGuest } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');

  useEffect(() => {
    loadAudits();
  }, [industryFilter]);

  const loadAudits = async () => {
    try {
      const data = await getAudits(industryFilter !== 'all' ? industryFilter : null);
      setAudits(data);
    } catch (error) {
      toast.error('Failed to load audits');
    } finally {
      setLoading(false);
    }
  };

  const filteredAudits = audits.filter(a => 
    a.title.toLowerCase().includes(search.toLowerCase()) ||
    a.area.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audits-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">6S Audits</h1>
            <p className="text-muted-foreground font-body mt-1">
              Workplace organization assessments with Safety as the sixth pillar
            </p>
          </div>
          <Button asChild data-testid="new-audit-btn" className={isGuest ? 'hidden' : ''}>
            <Link to="/audits/new">
              <Plus className="h-4 w-4 mr-2" /> New Audit
            </Link>
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search audits..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-audits"
            />
          </div>
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-industry">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map((ind) => (
                <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Audits Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredAudits.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAudits.map((audit) => {
              const scoreStyle = getScoreColor(audit.percentage);
              return (
                <div 
                  key={audit.audit_id} 
                  className={`block ${isGuest ? 'cursor-default' : 'cursor-pointer'}`}
                  data-testid={`audit-card-${audit.audit_id}`}
                  onClick={() => { if (!isGuest) { window.location.href = `/audits/${audit.audit_id}`; } }}
                >
                  <Card className="h-full card-hover">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <Badge className="capitalize">{audit.industry}</Badge>
                        <Badge variant={audit.status === 'completed' ? 'default' : 'secondary'}>
                          {audit.status === 'completed' ? (
                            <><CheckCircle2 className="h-3 w-3 mr-1" /> Completed</>
                          ) : (
                            <><AlertCircle className="h-3 w-3 mr-1" /> In Progress</>
                          )}
                        </Badge>
                      </div>
                      <CardTitle className="mt-3 line-clamp-1">{audit.title}</CardTitle>
                      <CardDescription>Area: {audit.area}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center gap-4">
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${scoreStyle.bg}`}>
                          <span className={`text-2xl font-bold ${scoreStyle.text}`}>
                            {Math.round(audit.percentage)}%
                          </span>
                        </div>
                        <div>
                          <p className={`font-semibold ${scoreStyle.text}`}>{scoreStyle.label}</p>
                          <p className="text-sm text-muted-foreground">
                            Score: {audit.total_score}/{audit.max_score}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Auditor: {audit.auditor}
                          </p>
                          {audit.scheduled_date && (
                            <p className="text-xs text-muted-foreground mt-1">
                              <Clock className="inline h-3 w-3 mr-1" />
                              Scheduled: {audit.scheduled_date}
                              {audit.recurrence && audit.recurrence !== 'none' && (
                                <span className="ml-1 text-cyan-600">({audit.recurrence})</span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        ) : (
          <Card className="py-16">
            <CardContent className="text-center">
              <ClipboardCheck className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No audits found</h3>
              <p className="text-muted-foreground font-body mb-4">
                {emptyStateMsg(isGuest, search, 'Start your first 6S audit')}
              </p>
              {!isGuest && (
                <Button asChild>
                  <Link to="/audits/new">
                    <Plus className="h-4 w-4 mr-2" /> Start Audit
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

// Create Audit Page
export const CreateAuditPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    area: '',
    auditor: user?.name || '',
    industry: 'logistics',
    scheduled_date: '',
    recurrence: 'none',
    reminder_days: 3
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const audit = await createAudit(formData);
      toast.success('Audit created successfully!');
      navigate(`/audits/${audit.audit_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="create-audit-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/audits')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">New 6S Audit</h1>
            <p className="text-muted-foreground font-body">Assess workplace organization and safety</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Audit Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Monthly Warehouse 6S Audit"
                  required
                  data-testid="audit-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="area">Area Being Audited *</Label>
                <Input
                  id="area"
                  value={formData.area}
                  onChange={(e) => handleChange('area', e.target.value)}
                  placeholder="e.g., Shipping Dock A, Assembly Line 3"
                  required
                  data-testid="audit-area"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="auditor">Auditor Name *</Label>
                  <Input
                    id="auditor"
                    value={formData.auditor}
                    onChange={(e) => handleChange('auditor', e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="audit-auditor"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                    <SelectTrigger data-testid="audit-industry">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scheduling */}
          <Card>
            <CardHeader>
              <CardTitle>Scheduling</CardTitle>
              <CardDescription>Set a date and optional recurrence for this audit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="scheduled_date">Scheduled Date</Label>
                <Input
                  id="scheduled_date"
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => handleChange('scheduled_date', e.target.value)}
                  data-testid="audit-scheduled-date"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recurrence">Recurrence</Label>
                  <Select value={formData.recurrence} onValueChange={(v) => handleChange('recurrence', v)}>
                    <SelectTrigger data-testid="audit-recurrence">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Recurrence</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reminder">Reminder (days before)</Label>
                  <Select value={String(formData.reminder_days)} onValueChange={(v) => handleChange('reminder_days', parseInt(v))}>
                    <SelectTrigger data-testid="audit-reminder">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day before</SelectItem>
                      <SelectItem value="3">3 days before</SelectItem>
                      <SelectItem value="7">1 week before</SelectItem>
                      <SelectItem value="14">2 weeks before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/audits')}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-audit">
              {loading ? 'Creating...' : 'Start Audit'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

// Audit Detail Page
export const AuditDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAudit();
  }, [id]);

  const loadAudit = async () => {
    try {
      const data = await getAudit(id);
      setAudit(data);
    } catch (error) {
      toast.error('Failed to load audit');
      navigate('/audits');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = async (categoryKey, score) => {
    setSaving(true);
    try {
      const updatedCategories = {
        ...audit.categories,
        [categoryKey]: { ...audit.categories[categoryKey], score }
      };
      await updateAudit(id, { categories: updatedCategories });
      
      // Recalculate totals
      const totalScore = Object.values(updatedCategories).reduce((sum, cat) => sum + (cat.score || 0), 0);
      const percentage = (totalScore / 30) * 100;
      
      setAudit({ ...audit, categories: updatedCategories, total_score: totalScore, percentage });
    } catch (error) {
      toast.error('Failed to update score');
    } finally {
      setSaving(false);
    }
  };

  const handleNotesChange = async (categoryKey, notes) => {
    const updatedCategories = {
      ...audit.categories,
      [categoryKey]: { ...audit.categories[categoryKey], notes }
    };
    setAudit({ ...audit, categories: updatedCategories });
  };

  const saveNotes = async (categoryKey) => {
    setSaving(true);
    try {
      await updateAudit(id, { categories: audit.categories });
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const completeAudit = async () => {
    setSaving(true);
    try {
      await updateAudit(id, { status: 'completed', completed_at: new Date().toISOString() });
      setAudit({ ...audit, status: 'completed' });
      toast.success('Audit completed!');
    } catch (error) {
      toast.error('Failed to complete audit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this audit?')) return;
    
    try {
      await deleteAudit(id);
      toast.success('Audit deleted');
      navigate('/audits');
    } catch (error) {
      toast.error('Failed to delete audit');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this audit? It can be restored from the Archive page.')) return;
    try {
      await archiveResource('audit', id);
      toast.success('Audit archived');
      navigate('/audits');
    } catch (error) {
      toast.error('Failed to archive audit');
    }
  };

  const canEdit = isAdmin || audit?.created_by === user?.user_id;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!audit) return null;

  const scoreStyle = getScoreColor(audit.percentage);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/audits')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="capitalize">{audit.industry}</Badge>
                <Badge variant={audit.status === 'completed' ? 'default' : 'secondary'}>
                  {audit.status}
                </Badge>
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{audit.title}</h1>
              <p className="text-muted-foreground font-body mt-1">Area: {audit.area} | Auditor: {audit.auditor}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {canEdit && audit.status !== 'completed' && (
              <Button onClick={completeAudit} disabled={saving} data-testid="complete-audit-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Complete Audit
              </Button>
            )}
            {canEdit && audit.status === 'completed' && (
              <Button variant="outline" size="sm" onClick={handleArchive} data-testid="archive-audit-btn">
                <Archive className="h-4 w-4 mr-2" /> Archive
              </Button>
            )}
            {isAdmin && (
              <Button variant="destructive" size="icon" onClick={handleDelete} data-testid="delete-audit-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Score Overview */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-8">
              <div className={`w-32 h-32 rounded-full flex flex-col items-center justify-center shrink-0 ${scoreStyle.bg}`}>
                <span className={`text-4xl font-bold ${scoreStyle.text}`}>
                  {Math.round(audit.percentage)}%
                </span>
                <span className={`text-sm ${scoreStyle.text}`}>{scoreStyle.label}</span>
              </div>
              <div className="flex-1 w-full">
                <h3 className="font-semibold mb-4">Overall Score: {audit.total_score}/{audit.max_score}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                  {sixSCategories.map((cat) => {
                    const score = audit.categories?.[cat.key]?.score || 0;
                    return (
                      <div key={cat.key} className="text-center p-3 rounded-lg border border-border bg-muted/30">
                        <div className={`text-2xl font-bold ${auditScoreColor(score)}`}>
                          {score}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{cat.label.split(' ')[0]}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category Scores */}
        <div className="grid md:grid-cols-2 gap-6">
          {sixSCategories.map((cat) => (
            <Card key={cat.key}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="text-2xl opacity-20">{cat.icon}</span>
                      {cat.label}
                    </CardTitle>
                    <CardDescription>{cat.description}</CardDescription>
                  </div>
                  <div className={`text-3xl font-bold ${auditScoreColor(audit.categories?.[cat.key]?.score || 0)}`}>
                    {audit.categories?.[cat.key]?.score || 0}/5
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {canEdit && audit.status !== 'completed' && (
                  <div className="space-y-2">
                    <Label>Score</Label>
                    <Slider
                      value={[audit.categories?.[cat.key]?.score || 0]}
                      onValueChange={([v]) => handleScoreChange(cat.key, v)}
                      max={5}
                      step={1}
                      disabled={saving}
                      data-testid={`score-${cat.key}`}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0 - Poor</span>
                      <span>5 - Excellent</span>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={audit.categories?.[cat.key]?.notes || ''}
                    onChange={(e) => handleNotesChange(cat.key, e.target.value)}
                    placeholder="Add observations..."
                    rows={3}
                    className="resize-none break-words whitespace-pre-wrap"
                    disabled={!canEdit || audit.status === 'completed'}
                    data-testid={`notes-${cat.key}`}
                  />
                  {canEdit && audit.status !== 'completed' && (
                    <Button size="sm" variant="outline" onClick={() => saveNotes(cat.key)} disabled={saving}>
                      <Save className="h-3 w-3 mr-1" /> Save Notes
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AuditsListPage;
